import { useState } from 'react';
import { Link2, SkipForward, Loader2 } from 'lucide-react';
import { onboardingApi } from '@/lib/api';

interface Props {
  tenantId: string;
  token:    string;
  onNext:   () => void;
  onBack:   () => void;
}

const FACILITY_TYPES  = ['Hospital', 'Clinic', 'Nursing Home', 'Diagnostic Lab', 'Pharmacy', 'Other'];
const OWNERSHIP_TYPES = ['Private', 'Government', 'Trust / NGO', 'Public-Private Partnership'];

export default function Step3HFR({ tenantId, token, onNext, onBack }: Props) {
  const [linking, setLinking]     = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError]         = useState<string | null>(null);
  const [form, setForm] = useState({
    facilityName:  '',
    facilityType:  'Hospital',
    ownershipType: 'Private',
  });

  async function skip() {
    setSubmitting(true);
    try {
      await onboardingApi.step3(tenantId, token, { skipped: true });
      onNext();
    } catch {
      onNext(); // non-critical — advance anyway
    } finally {
      setSubmitting(false);
    }
  }

  async function submitHFR() {
    if (!form.facilityName.trim()) {
      setError('Enter the facility name as it should appear in ABDM records.');
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      await onboardingApi.step3(tenantId, token, { skipped: false, ...form });
      onNext();
    } catch (e: any) {
      setError(e.response?.data?.error ?? 'HFR submission failed. You can skip and link later.');
    } finally {
      setSubmitting(false);
    }
  }

  const input = 'w-full px-4 py-2.5 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-orange/30 focus:border-orange';

  return (
    <div className="space-y-6">
      {/* What is HFR */}
      <div className="bg-navy/5 rounded-xl p-4 text-sm text-navy/70 space-y-1">
        <p><strong className="text-navy">Health Facility Registry (HFR)</strong> is part of India's ABDM digital health infrastructure.</p>
        <p>Linking gives your hospital a unique HFR ID, enables ABHA-based patient lookup at reception, and is required for Ayushman Bharat claim integration.</p>
      </div>

      {/* Toggle */}
      <div className="grid grid-cols-2 gap-3">
        <button type="button"
          onClick={() => setLinking(false)}
          className={`p-4 rounded-xl border-2 text-left transition-colors
            ${!linking ? 'border-orange bg-orange/5' : 'border-gray-200 hover:border-gray-300'}`}>
          <SkipForward size={20} className={!linking ? 'text-orange' : 'text-gray-400'}/>
          <div className={`text-sm font-semibold mt-2 ${!linking ? 'text-navy' : 'text-gray-500'}`}>Skip for now</div>
          <div className="text-xs text-gray-400 mt-1">Operate in local mode. Link HFR anytime from Settings.</div>
        </button>
        <button type="button"
          onClick={() => setLinking(true)}
          className={`p-4 rounded-xl border-2 text-left transition-colors
            ${linking ? 'border-orange bg-orange/5' : 'border-gray-200 hover:border-gray-300'}`}>
          <Link2 size={20} className={linking ? 'text-orange' : 'text-gray-400'}/>
          <div className={`text-sm font-semibold mt-2 ${linking ? 'text-navy' : 'text-gray-500'}`}>Link HFR now</div>
          <div className="text-xs text-gray-400 mt-1">Submit facility details — NHA approval in 2–5 days.</div>
        </button>
      </div>

      {/* HFR form */}
      {linking && (
        <div className="space-y-4 border border-gray-200 rounded-xl p-4">
          <div>
            <label className="block text-sm font-medium text-navy mb-1.5">Facility Name *</label>
            <input
              value={form.facilityName}
              onChange={e => setForm(f => ({ ...f, facilityName: e.target.value }))}
              placeholder="As it should appear in ABDM records"
              className={input}
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-navy mb-1.5">Facility Type</label>
              <select value={form.facilityType}
                onChange={e => setForm(f => ({ ...f, facilityType: e.target.value }))}
                className={input}>
                {FACILITY_TYPES.map(t => <option key={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-navy mb-1.5">Ownership</label>
              <select value={form.ownershipType}
                onChange={e => setForm(f => ({ ...f, ownershipType: e.target.value }))}
                className={input}>
                {OWNERSHIP_TYPES.map(t => <option key={t}>{t}</option>)}
              </select>
            </div>
          </div>
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 text-clinical-danger text-sm rounded-lg px-4 py-3">
          {error}
        </div>
      )}

      <div className="flex gap-3">
        <button type="button" onClick={onBack}
          className="flex-1 py-3 rounded-lg border border-gray-200 text-navy text-sm font-medium hover:bg-gray-50 transition-colors">
          Back
        </button>
        <button type="button"
          onClick={linking ? submitHFR : skip}
          disabled={submitting}
          className="flex-1 py-3 rounded-lg bg-orange text-navy font-semibold text-sm
                     hover:bg-orange-dark transition-colors disabled:opacity-50
                     flex items-center justify-center gap-2">
          {submitting && <Loader2 size={16} className="animate-spin"/>}
          {linking ? 'Submit HFR Request' : 'Skip & Continue'}
        </button>
      </div>
    </div>
  );
}
